import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceApplicationForm } from '@/components/AbsenceApplication/AbsenceApplicationForm';
import { AbsenceDetailView } from '@/components/AbsenceApplication/AbsenceDetailView';
import { AbsenceHistoryItem } from '@/components/AbsenceApplication/AbsenceHistoryItem';
import { useAbsenceApplications } from '@/components/AbsenceApplication/hooks/useAbsenceApplications';
import { useNonAttendanceProperties } from '@/components/AbsenceApplication/hooks/useNonAttendanceProperties';
import type {
  NonAttendanceApplicationDetail,
  SubmitNonAttendanceApplicationRequest,
} from '@/components/AbsenceApplication/types';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { Close } from '@/components/Icons/Close';
import { PlusSign } from '@/components/Icons/PlusSign';
import { Modal } from '@/components/Modal';
import { useProfileRestriction } from '@/components/ProfileRestriction/useProfileRestriction';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import {
  BorderRadius,
  ButtonVariant,
  ModalSize,
  Size,
} from '@/types/components';

export enum ApplicationsView {
  List = 'list',
  Form = 'form',
  Detail = 'detail',
}

const LIST_MODAL_CLASS_NAME = 'md:h-[85svh] md:w-[min(853px,calc(100vw-80px))]';
const MOBILE_VIEW_ANIMATION_CLASS_NAME = 'max-md:animate-zoom-in';

interface Props {
  initialView: ApplicationsView;
  onClose: () => void;
  onOverlap: () => void;
  onSuccess: (
    application: NonAttendanceApplicationDetail,
    payload: SubmitNonAttendanceApplicationRequest,
  ) => void;
  onServerError: (payload: SubmitNonAttendanceApplicationRequest) => void;
}

export const AbsenceApplicationsModal: React.FC<Props> = ({
  initialView,
  onClose,
  onOverlap,
  onSuccess,
  onServerError,
}) => {
  const { t } = useTranslation();
  const { guardAction } = useProfileRestriction();
  const isMobile = useIsMobile();
  const contentRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState(initialView);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<
    number | null
  >(null);
  const { progress: scrollProgress, handleScroll } = useScrollFadeProgress();

  const isListOpen = view === ApplicationsView.List;
  const isFormOpen = view === ApplicationsView.Form;

  const { data, isLoading } = useAbsenceApplications({ enabled: isListOpen });
  const { data: properties, isFetching: isFetchingProperties } =
    useNonAttendanceProperties({ enabled: isFormOpen });

  const applications = data ?? [];
  const current = applications.filter((application) => !application.isExpired);
  const past = applications.filter((application) => application.isExpired);

  const modalLabel = isFormOpen
    ? t('absenceApplication.title')
    : t('absenceHistory.title');
  const modalSize = isFormOpen ? ModalSize.Large : ModalSize.XLarge;
  const modalClassName = isFormOpen ? undefined : LIST_MODAL_CLASS_NAME;
  const isModalLoading = isFormOpen
    ? isFetchingProperties || !properties
    : isListOpen && (isLoading || !data);

  const mobileViewAnimationClassName = hasNavigated
    ? MOBILE_VIEW_ANIMATION_CLASS_NAME
    : '';

  const goTo = (nextView: ApplicationsView) => {
    setHasNavigated(true);
    setView(nextView);
  };

  const openList = () => goTo(ApplicationsView.List);

  const openForm = () => guardAction(() => goTo(ApplicationsView.Form));

  const openDetail = (applicationId: number) => {
    setSelectedApplicationId(applicationId);
    goTo(ApplicationsView.Detail);
  };

  useLayoutEffect(() => {
    const content = contentRef.current;

    if (!content || !hasNavigated || isMobile) {
      return;
    }

    content.style.animation = 'none';
    content.getBoundingClientRect();
    content.style.animation = '';
  }, [view, hasNavigated, isMobile]);

  return (
    <Modal
      isOpen
      label={modalLabel}
      className={modalClassName}
      size={modalSize}
      isLoading={isModalLoading}
      contentRef={contentRef}
      onClose={onClose}
    >
      {isFormOpen && properties && (
        <div className={`flex h-full flex-col ${mobileViewAnimationClassName}`}>
          <AbsenceApplicationForm
            properties={properties}
            onClose={onClose}
            onBack={openList}
            onOverlap={onOverlap}
            onSuccess={onSuccess}
            onServerError={onServerError}
          />
        </div>
      )}

      {view === ApplicationsView.Detail && selectedApplicationId !== null && (
        <div className={`flex h-full flex-col ${mobileViewAnimationClassName}`}>
          <AbsenceDetailView
            applicationId={selectedApplicationId}
            onBack={openList}
            onClose={onClose}
          />
        </div>
      )}

      {isListOpen && (
        <div className={`flex h-full flex-col ${mobileViewAnimationClassName}`}>
          <div className="relative flex shrink-0 items-center justify-between bg-background px-4 pt-[calc(16px+env(safe-area-inset-top))] pb-3 md:px-5 md:pt-5 md:pb-4">
            <button
              type="button"
              aria-label={t('global.back')}
              onClick={onClose}
              className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:hidden"
            >
              <ArrowLeft />
            </button>
            <h2
              style={{ opacity: scrollProgress }}
              className="text-callout-16 pointer-events-none absolute left-1/2 -translate-x-1/2 text-foreground transition-opacity duration-75 md:hidden"
            >
              {t('absenceHistory.title')}
            </h2>
            <h2 className="text-headline-20 hidden text-foreground md:block">
              {t('absenceHistory.title')}
            </h2>
            <button
              type="button"
              aria-label={t('global.close')}
              onClick={onClose}
              className="hidden size-6 shrink-0 cursor-pointer items-center justify-center text-foreground md:flex"
            >
              <Close />
            </button>
          </div>

          <AutoHideScrollArea
            hasTopShadow
            hasBottomShadow
            className="flex min-h-0 flex-1 flex-col"
            onScroll={handleScroll}
          >
            <div className="flex flex-col gap-5 px-4 pb-[calc(20px+env(safe-area-inset-bottom))] md:px-5 md:pb-5">
              <h2
                style={{ opacity: 1 - scrollProgress }}
                className="text-title-48 text-foreground transition-opacity duration-75 md:hidden"
              >
                {t('absenceHistory.title')}
              </h2>

              <Button
                variant={ButtonVariant.Primary}
                size={Size.Large}
                radius={BorderRadius.Small}
                className="h-12 w-full shrink-0 md:h-16 md:rounded-2xl"
                onClick={openForm}
              >
                <PlusSign strokeWidth="1.4" className="size-4.5" />
                {t('global.reportAbsence')}
              </Button>

              <section className="flex flex-col gap-3">
                <h3 className="text-callout-16 text-foreground">
                  {t('absenceHistory.current')}
                </h3>
                {current.length === 0 && (
                  <EmptyState label={t('absenceHistory.emptyCurrent')} />
                )}
                {current.length > 0 && (
                  <div className="flex flex-col gap-2 md:gap-3">
                    {current.map((application) => (
                      <AbsenceHistoryItem
                        key={application.id}
                        application={application}
                        onClick={openDetail}
                      />
                    ))}
                  </div>
                )}
              </section>

              <section className="flex flex-col gap-3">
                <h3 className="text-callout-16 text-foreground">
                  {t('absenceHistory.past')}
                </h3>
                {past.length === 0 && (
                  <EmptyState label={t('absenceHistory.emptyPast')} />
                )}
                {past.length > 0 && (
                  <div className="flex flex-col gap-2 md:gap-3">
                    {past.map((application) => (
                      <AbsenceHistoryItem
                        key={application.id}
                        application={application}
                        onClick={openDetail}
                      />
                    ))}
                  </div>
                )}
              </section>
            </div>
          </AutoHideScrollArea>
        </div>
      )}
    </Modal>
  );
};
